import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared, type ErrorView } from '@krp/client-runtime'

import type { CommandStatus } from '../../../shared/model/commandOutcome'
import { commandKey, lineKey, normalizeRid, ownedBy, supplyKey } from './keys'
import { MAX_SNAPSHOT_PAGES, MAX_SUPPLY_NUMBER_LENGTH } from './limits'
import {
  EMPTY_FORMS,
  EMPTY_LINE_FILTER,
  EMPTY_LIST_FILTER,
  type AddLineForm,
  type CreateForm,
  type HeaderField,
  type LineField,
  type LineFilter,
  type SuppliesIntentState,
  type SupplyCommand,
  type SupplyCommandResult,
  type SupplyForms,
  type SupplyListFilter,
  type SentIdentity,
  type SupplySection,
} from './types'

/**
 * Намерение поставок 1С (срок `screen`): фильтры, выбранный раздел, дельты
 * правок (шапка, строки, распределение), формы и идентичность команд. Ответы
 * службы сюда не кладутся — они в кэше RTK Query; черновик хранит ТОЛЬКО то,
 * что человек набрал, и версию, с которой начал (optimistic concurrency).
 */
const initialState: SuppliesIntentState = {
  listFilter: {},
  listPages: {},
  section: {},
  lineFilter: {},
  headerDrafts: {},
  lineDrafts: {},
  allocationDrafts: {},
  createForms: {},
  forms: {},
  commands: {},
  historyPages: {},
  savedVersions: {},
}

interface SupplyRef {
  resourceId: string
  supplyId: string
}

interface LineRef extends SupplyRef {
  lineId: string
}

const busy = (status: CommandStatus | undefined) => status === 'sending' || status === 'unknown'

function formsOf(state: SuppliesIntentState, key: string): SupplyForms {
  const current = state.forms[key]
  if (current !== undefined) return current
  const created: SupplyForms = { ...EMPTY_FORMS, addLine: { ...EMPTY_FORMS.addLine }, prices: {}, overrideProduct: {} }
  state.forms[key] = created
  return created
}

export interface CommandSettled {
  key: string
  operationId: string
  status: Exclude<CommandStatus, 'sending'>
  resendable: boolean
  error: ErrorView | null
  result: SupplyCommandResult | null
}

const suppliesSlice = createSlice({
  name: 'onec/supplies',
  initialState,
  reducers: {
    // --- список -----------------------------------------------------------------------
    listFilterChanged(state, action: PayloadAction<{ resourceId: string; patch: Partial<SupplyListFilter> }>) {
      const rid = normalizeRid(action.payload.resourceId)
      state.listFilter[rid] = { ...(state.listFilter[rid] ?? EMPTY_LIST_FILTER), ...action.payload.patch }
      // Новый фильтр — новый снимок: «показать ещё» начинается заново.
      delete state.listPages[rid]
    },
    listMoreRequested(state, action: PayloadAction<{ resourceId: string; pages: number }>) {
      state.listPages[normalizeRid(action.payload.resourceId)] = Math.min(Math.max(1, action.payload.pages), MAX_SNAPSHOT_PAGES)
    },

    // --- окно поставки ----------------------------------------------------------------
    sectionChosen(state, action: PayloadAction<SupplyRef & { section: SupplySection }>) {
      state.section[supplyKey(action.payload.resourceId, action.payload.supplyId)] = action.payload.section
    },
    lineFilterChanged(state, action: PayloadAction<SupplyRef & { patch: Partial<LineFilter> }>) {
      const key = supplyKey(action.payload.resourceId, action.payload.supplyId)
      state.lineFilter[key] = { ...(state.lineFilter[key] ?? EMPTY_LINE_FILTER), ...action.payload.patch }
    },
    historyMoreRequested(state, action: PayloadAction<SupplyRef & { pages: number }>) {
      state.historyPages[supplyKey(action.payload.resourceId, action.payload.supplyId)] = Math.min(Math.max(1, action.payload.pages), MAX_SNAPSHOT_PAGES)
    },

    // --- шапка: черновик с явным «Сохранить» ------------------------------------------
    headerEdited(state, action: PayloadAction<SupplyRef & { field: HeaderField; value: string; server: string }>) {
      const key = supplyKey(action.payload.resourceId, action.payload.supplyId)
      const draft = state.headerDrafts[key] ?? { values: {}, base: {} }
      const { field } = action.payload
      if (!(field in draft.base)) draft.base[field] = action.payload.server
      draft.values[field] = action.payload.value
      state.headerDrafts[key] = draft
    },
    headerDiscarded(state, action: PayloadAction<SupplyRef>) {
      delete state.headerDrafts[supplyKey(action.payload.resourceId, action.payload.supplyId)]
    },
    /** «Оставить моё» после конфликта: основа правки — серверные значения, которые человек увидел. */
    headerRebased(state, action: PayloadAction<SupplyRef & { server: Partial<Record<HeaderField, string>> }>) {
      const draft = state.headerDrafts[supplyKey(action.payload.resourceId, action.payload.supplyId)]
      if (draft === undefined) return
      for (const field of Object.keys(draft.base) as HeaderField[]) {
        const server = action.payload.server[field]
        if (server !== undefined) draft.base[field] = server
      }
    },

    // --- строки: дельта, сохраняется debounce-listener-ом -----------------------------
    lineEdited(
      state,
      action: PayloadAction<LineRef & { field: LineField; value: string; version: number; configurationVersion: string }>,
    ) {
      const { resourceId, supplyId, lineId, field, value, version, configurationVersion } = action.payload
      const key = lineKey(resourceId, supplyId, lineId)
      const draft = state.lineDrafts[key] ?? {
        key,
        resourceId: normalizeRid(resourceId),
        supplyId: supplyId.toLowerCase(),
        lineId: lineId.toLowerCase(),
        values: {},
        baseVersion: Math.max(version, state.savedVersions[key] ?? 0),
        allocationPolicy: null,
        configurationVersion,
        state: 'pending' as const,
        error: null,
        revision: 0,
        sent: null,
      }
      draft.values[field] = value
      draft.revision += 1
      // Конфликт держится, пока человек не решит: новый набор не отправляется молча поверх чужой версии.
      if (draft.state !== 'conflict') {
        draft.state = 'pending'
        draft.error = null
      }
      state.lineDrafts[key] = draft
    },
    lineSaveStarted(state, action: PayloadAction<{ key: string; sent: SentIdentity }>) {
      const draft = state.lineDrafts[action.payload.key]
      if (draft === undefined || draft.revision !== action.payload.sent.revision) return
      draft.state = 'saving'
      draft.sent = action.payload.sent
    },
    /** Ответ сохранения. Если человек набрал ещё, черновик остаётся на новой версии сервера. */
    lineSaveSettled(
      state,
      action: PayloadAction<{ key: string; revision: number; ok: boolean; conflict: boolean; unknown?: boolean; error: ErrorView | null; version: number | null }>,
    ) {
      const draft = state.lineDrafts[action.payload.key]
      if (draft === undefined) return
      const { ok, conflict, error, revision, version } = action.payload
      if (ok) {
        ownWrite(state, action.payload.key, draft.sent?.baseVersion ?? draft.baseVersion, version, 'line')
        if (draft.revision === revision) {
          delete state.lineDrafts[action.payload.key]
          return
        }
        if (version !== null) draft.baseVersion = version
        draft.state = 'pending'
        draft.allocationPolicy = null
        draft.sent = null
        return
      }
      draft.state = conflict ? 'conflict' : action.payload.unknown === true ? 'unknown' : 'failed'
      draft.error = error
    },
    lineDraftDiscarded(state, action: PayloadAction<LineRef>) {
      delete state.lineDrafts[lineKey(action.payload.resourceId, action.payload.supplyId, action.payload.lineId)]
    },
    /** «Оставить моё»: правка ложится на версию строки, которую человек теперь видит. */
    lineDraftRebased(
      state,
      action: PayloadAction<LineRef & { version: number; configurationVersion: string; allocationPolicy?: 'Clear' | 'Proportional' }>,
    ) {
      const draft = state.lineDrafts[lineKey(action.payload.resourceId, action.payload.supplyId, action.payload.lineId)]
      if (draft === undefined) return
      draft.baseVersion = action.payload.version
      draft.configurationVersion = action.payload.configurationVersion
      if (action.payload.allocationPolicy !== undefined) draft.allocationPolicy = action.payload.allocationPolicy
      draft.state = 'pending'
      draft.error = null
      draft.revision += 1
    },

    /** «Повторить» после отказа или неизвестного исхода: тот же набор, та же идентичность отправки. */
    lineDraftRetried(state, action: PayloadAction<LineRef>) {
      const draft = state.lineDrafts[lineKey(action.payload.resourceId, action.payload.supplyId, action.payload.lineId)]
      if (draft === undefined || (draft.state !== 'failed' && draft.state !== 'unknown')) return
      draft.state = 'pending'
      draft.error = null
    },

    // --- распределение: ячейка — дельта строки ----------------------------------------
    allocationEdited(
      state,
      action: PayloadAction<
        LineRef & { warehouseKey: string; value: string; version: number; base: Record<string, number>; configurationVersion: string }
      >,
    ) {
      const { resourceId, supplyId, lineId, warehouseKey, value, version, base, configurationVersion } = action.payload
      const key = lineKey(resourceId, supplyId, lineId)
      const draft = state.allocationDrafts[key] ?? {
        key,
        resourceId: normalizeRid(resourceId),
        supplyId: supplyId.toLowerCase(),
        lineId: lineId.toLowerCase(),
        cells: {},
        base,
        baseVersion: Math.max(version, state.savedVersions[key] ?? 0),
        configurationVersion,
        state: 'pending' as const,
        error: null,
        revision: 0,
        sent: null,
      }
      draft.cells[warehouseKey.toLowerCase()] = value
      draft.revision += 1
      if (draft.state !== 'conflict') {
        draft.state = 'pending'
        draft.error = null
      }
      state.allocationDrafts[key] = draft
    },
    allocationSaveStarted(state, action: PayloadAction<{ key: string; sent: SentIdentity }>) {
      const draft = state.allocationDrafts[action.payload.key]
      if (draft === undefined || draft.revision !== action.payload.sent.revision) return
      draft.state = 'saving'
      draft.sent = action.payload.sent
    },
    allocationSaveSettled(
      state,
      action: PayloadAction<{ key: string; revision: number; ok: boolean; conflict: boolean; unknown?: boolean; error: ErrorView | null; version: number | null }>,
    ) {
      const draft = state.allocationDrafts[action.payload.key]
      if (draft === undefined) return
      if (action.payload.ok) {
        ownWrite(state, action.payload.key, draft.sent?.baseVersion ?? draft.baseVersion, action.payload.version, 'allocation')
        if (draft.revision === action.payload.revision) {
          delete state.allocationDrafts[action.payload.key]
          return
        }
        // Человек набрал ещё, пока летел запрос: версия строки теперь другая — следующий набор ждёт перечитанной.
        if (action.payload.version !== null) draft.baseVersion = action.payload.version
        draft.state = 'pending'
        draft.sent = null
        return
      }
      draft.state = action.payload.conflict ? 'conflict' : action.payload.unknown === true ? 'unknown' : 'failed'
      draft.error = action.payload.error
    },
    allocationDraftDiscarded(state, action: PayloadAction<LineRef>) {
      delete state.allocationDrafts[lineKey(action.payload.resourceId, action.payload.supplyId, action.payload.lineId)]
    },
    allocationDraftRebased(
      state,
      action: PayloadAction<LineRef & { version: number; base: Record<string, number>; configurationVersion: string }>,
    ) {
      const draft = state.allocationDrafts[lineKey(action.payload.resourceId, action.payload.supplyId, action.payload.lineId)]
      if (draft === undefined) return
      draft.baseVersion = action.payload.version
      draft.configurationVersion = action.payload.configurationVersion
      draft.base = action.payload.base
      draft.state = 'pending'
      draft.error = null
      draft.revision += 1
    },

    allocationDraftRetried(state, action: PayloadAction<LineRef>) {
      const draft = state.allocationDrafts[lineKey(action.payload.resourceId, action.payload.supplyId, action.payload.lineId)]
      if (draft === undefined || (draft.state !== 'failed' && draft.state !== 'unknown')) return
      draft.state = 'pending'
      draft.error = null
    },

    // --- формы -------------------------------------------------------------------------
    createFormChanged(state, action: PayloadAction<{ resourceId: string; patch: Partial<CreateForm>; today: string }>) {
      const rid = normalizeRid(action.payload.resourceId)
      const current = state.createForms[rid] ?? emptyCreateForm(action.payload.today)
      state.createForms[rid] = { ...current, ...action.payload.patch }
    },
    createFormReset(state, action: PayloadAction<{ resourceId: string }>) {
      delete state.createForms[normalizeRid(action.payload.resourceId)]
    },
    addLineChanged(state, action: PayloadAction<SupplyRef & { patch: Partial<AddLineForm> }>) {
      const forms = formsOf(state, supplyKey(action.payload.resourceId, action.payload.supplyId))
      forms.addLine = { ...forms.addLine, ...action.payload.patch }
    },
    formChanged(
      state,
      action: PayloadAction<
        SupplyRef & { patch: Partial<Pick<SupplyForms, 'invoiceFileId' | 'invoiceMode' | 'priceFactor' | 'fillWarehouseKey'>> }
      >,
    ) {
      const forms = formsOf(state, supplyKey(action.payload.resourceId, action.payload.supplyId))
      Object.assign(forms, action.payload.patch)
    },
    priceTyped(state, action: PayloadAction<SupplyRef & { key: string; value: string }>) {
      formsOf(state, supplyKey(action.payload.resourceId, action.payload.supplyId)).prices[action.payload.key] = action.payload.value
    },
    pricesCleared(state, action: PayloadAction<SupplyRef>) {
      formsOf(state, supplyKey(action.payload.resourceId, action.payload.supplyId)).prices = {}
    },
    overrideTyped(state, action: PayloadAction<LineRef & { value: string }>) {
      formsOf(state, supplyKey(action.payload.resourceId, action.payload.supplyId)).overrideProduct[
        action.payload.lineId.toLowerCase()
      ] = action.payload.value
    },

    // --- команды ------------------------------------------------------------------------
    commandStarted(state, action: PayloadAction<SupplyCommand>) {
      const current = state.commands[action.payload.key]
      // Пока прежняя команда в пути или её исход неизвестен, новую не начинаем: двойная запись.
      if (current !== undefined && busy(current.status)) return
      state.commands[action.payload.key] = action.payload
    },
    commandSettled(state, action: PayloadAction<CommandSettled>) {
      const command = state.commands[action.payload.key]
      if (command === undefined || command.operationId !== action.payload.operationId) return
      command.status = action.payload.status
      command.resendable = action.payload.resendable
      command.error = action.payload.error
      command.result = action.payload.result
    },
    commandResent(state, action: PayloadAction<{ key: string }>) {
      const command = state.commands[action.payload.key]
      if (command === undefined) return
      command.status = 'sending'
      command.error = null
    },
    commandDismissed(state, action: PayloadAction<{ key: string }>) {
      const command = state.commands[action.payload.key]
      // Неизвестный исход не скрывается: забыть ключ — значит потерять способ проверить.
      if (command === undefined || busy(command.status)) return
      delete state.commands[action.payload.key]
    },
    /**
     * Исход проведения выяснен чтением статуса (а не повтором POST): журнал
     * саги показал попытку — команда больше не «неизвестна».
     */
    postingObserved(state, action: PayloadAction<SupplyRef & { attemptNumber: number }>) {
      const command = state.commands[commandKey(action.payload.resourceId, action.payload.supplyId, 'post')]
      if (command === undefined || command.status !== 'unknown') return
      command.status = 'succeeded'
      command.resendable = false
      command.error = null
      command.result = { kind: 'posting', posting: { attemptNumber: action.payload.attemptNumber } }
    },
    /**
     * Журнал саги попытки новее той, что была до нажатия, не знает: запрос до
     * службы не дошёл. Исход выяснен чтением — «не выполнено», можно провести снова.
     */
    postingNotObserved(state, action: PayloadAction<SupplyRef>) {
      const command = state.commands[commandKey(action.payload.resourceId, action.payload.supplyId, 'post')]
      if (command === undefined || command.status !== 'unknown') return
      command.status = 'failed'
      command.resendable = false
      command.error = {
        kind: 'client',
        code: 'posting-not-started',
        message: 'Журнал проведения новой попытки не знает: запрос до службы не дошёл. Проверьте предпросмотр и проведите снова.',
        correlationId: command.error?.correlationId ?? null,
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      const rid = action.payload.resourceId
      for (const bucket of [
        state.section,
        state.lineFilter,
        state.headerDrafts,
        state.lineDrafts,
        state.allocationDrafts,
        state.forms,
        state.commands,
      ]) {
        for (const key of Object.keys(bucket)) if (ownedBy(key, rid)) delete bucket[key]
      }
      for (const key of Object.keys(state.historyPages)) if (ownedBy(key, rid)) delete state.historyPages[key]
      for (const key of Object.keys(state.savedVersions)) if (ownedBy(key, rid)) delete state.savedVersions[key]
      delete state.listFilter[normalizeRid(rid)]
      delete state.listPages[normalizeRid(rid)]
      delete state.createForms[normalizeRid(rid)]
    })
  },
})

/**
 * Своя запись подтверждена: строка теперь в версии `version`. Черновик соседнего
 * вида (строка ↔ распределение) той же строки, начатый с той же версии, переезжает
 * на новую — иначе собственная правка выглядела бы чужой (409).
 */
function ownWrite(state: SuppliesIntentState, key: string, sentBase: number, version: number | null, from: 'line' | 'allocation') {
  if (version === null) return
  state.savedVersions[key] = Math.max(version, state.savedVersions[key] ?? 0)
  const other = from === 'line' ? state.allocationDrafts[key] : state.lineDrafts[key]
  if (other !== undefined && other.baseVersion === sentBase && other.state !== 'saving') other.baseVersion = version
}

export function emptyCreateForm(today: string): CreateForm {
  return {
    documentDate: today,
    warehouseKey: '',
    withoutSupplier: false,
    supplierKey: '',
    supplierName: '',
    supplierInvoiceNumber: '',
    comment: '',
  }
}

/** Снимок вкладки списка: только фильтр этой базы (не найденные поставки). */
export function decodeListFilter(raw: unknown): SupplyListFilter | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const value = raw as Record<string, unknown>
  const status = typeof value.status === 'string' ? value.status : ''
  const number = typeof value.number === 'string' ? value.number.slice(0, MAX_SUPPLY_NUMBER_LENGTH) : ''
  const allowed = ['', 'Draft', 'Imported', 'Matched', 'Priced', 'Allocated', 'Posting', 'Posted', 'PartiallyPosted', 'Failed']
  return allowed.includes(status) ? { status: status as SupplyListFilter['status'], number } : undefined
}

export function decodeSection(raw: unknown): SupplySection | undefined {
  return raw === 'lines' || raw === 'allocation' || raw === 'prices' || raw === 'posting' || raw === 'history' ? raw : undefined
}

export const {
  listFilterChanged,
  listMoreRequested,
  sectionChosen,
  lineFilterChanged,
  historyMoreRequested,
  headerEdited,
  headerDiscarded,
  headerRebased,
  lineEdited,
  lineSaveStarted,
  lineSaveSettled,
  lineDraftDiscarded,
  lineDraftRebased,
  lineDraftRetried,
  allocationEdited,
  allocationSaveStarted,
  allocationSaveSettled,
  allocationDraftDiscarded,
  allocationDraftRebased,
  allocationDraftRetried,
  createFormChanged,
  createFormReset,
  addLineChanged,
  formChanged,
  priceTyped,
  pricesCleared,
  overrideTyped,
  commandStarted,
  commandSettled,
  commandResent,
  commandDismissed,
  postingObserved,
  postingNotObserved,
} = suppliesSlice.actions
export const suppliesReducer = suppliesSlice.reducer
